import * as React from 'react'

import apis from '@app/api/api'

import Images from '@components/images/images'
import Button from '@components/button/button'
import { inProgressMarker, stoppedMarker } from '@components/marker/business'

import useI18n from '@store/i18n/useI18n'

import Theme from '@theme/Theme'
import styled from '@theme/styled-components'

import { motion, container, item } from '@utils/Motion'
import DownloadingComponent from '@utils/ImageDownloader'

import { FiChevronRight, FiChevronDown } from 'react-icons/fi'
import { BsDownload } from 'react-icons/bs'
import Loader from 'react-loader-spinner'
import Swal from 'sweetalert2'

interface Props {
  navigation: Navigation
  screenStatus: ScreenStatus
  business: BusinessDetails | undefined
  steps: BusinessStep[]
}

interface DeclarationPictureStepped extends DeclarationPicture {
  step: string
  theme: string
}

const BusinessFocusMenu = ({ navigation, screenStatus, business, steps }: Props) => {
  const i18n = useI18n()

  const [selectedStep, setSelectedStep] = React.useState<BusinessStep | undefined>(undefined)
  const [selectedTheme, setSelectedTheme] = React.useState<BusinessTheme | undefined>(undefined)
  const [downloading, setDownloading] = React.useState<boolean>(false)
  const [declarations, setDeclarations] = React.useState<Declaration[]>()

  React.useEffect(() => {
    setDeclarations(business?.declarations)
  }, [business])

  const selectedPhoto: DeclarationPictureStepped[] = React.useMemo(
    () =>
      declarations
        ? declarations.reduce((acc, cur) => {
            if (
              (!selectedStep || cur.description.idStep === selectedStep.id) &&
              (!selectedTheme || cur.description.idTheme === selectedTheme.id)
            ) {
              const sw: BusinessStep | undefined = steps.find((s) => cur.description.idStep === s.id)
              const tw: BusinessTheme | undefined = sw && sw.themes.find((t) => cur.description.idTheme === t.id)

              acc.push(
                ...cur.pictures.map((p) => ({ ...p, step: sw ? sw.wording : '/', theme: tw ? tw.wording : '/' }))
              )
            }
            return acc
          }, [] as DeclarationPictureStepped[])
        : [],
    [declarations, steps, selectedStep, selectedTheme]
  )

  const deletePicture = (imageId: string, declaration: Declaration) => {
    if (declaration) {
      const newDeclaration: Declaration = {
        ...declaration,
        pictures: declaration?.pictures.filter((oldPicture) => oldPicture.id !== imageId) ?? [],
      }

      if (newDeclaration) {
        apis.declaration
          .edit(newDeclaration)
          .then((res) => {
            setDeclarations(declarations ? [...declarations.filter((d) => d.id !== res.id), res] : [res])
            Swal.fire({
              title: i18n.t(`components.detailsChantier.alertDeleting.success.title`),
              text: i18n.t(`components.detailsChantier.alertDeleting.success.text`),
              icon: 'success',
            })
          })
          .catch(() => {
            Swal.fire({
              title: i18n.t(`components.detailsChantier.alertDeleting.failure.title`),
              text: i18n.t(`components.detailsChantier.alertDeleting.failure.text`),
              icon: 'error',
            })
          })
      }
    }
  }

  const confirmDeletePhoto = (imageId: string) => {
    if (business) {
      const oldDeclaration = business.declarations.find((d) => {
        return d.pictures.find((p) => p.id === imageId)
      })
      if (oldDeclaration) {
        return oldDeclaration?.breakpoint
          ? Swal.fire({
              title: i18n.t(`components.detailsChantier.alertDeleting.breakpoint.title`),
              text: i18n.t(`components.detailsChantier.alertDeleting.breakpoint.text`),
              icon: 'error',
            })
          : Swal.fire({
              title: i18n.t('components.detailsChantier.alertDeleting.title'),
              text: i18n.t('components.detailsChantier.alertDeleting.text'),
              showCancelButton: true,
              confirmButtonText: i18n.t('components.adminCompanies.alertCancelling.confirm'),
              confirmButtonColor: 'green',
              cancelButtonText: i18n.t('components.adminCompanies.alertCancelling.cancel'),
              cancelButtonColor: 'red',
            }).then((result) => {
              if (result.isConfirmed) {
                Swal.fire({
                  didOpen: () => {
                    Swal.showLoading()
                  },
                })
                deletePicture(imageId, oldDeclaration)
              }
            })
      }
    }
  }

  return (
    <Container>
      <ScreenTitleContainer>
        <ScreenTitle>{i18n.t('components.menu.chantierTitle')}</ScreenTitle>
        <Back onClick={() => navigation.goBack()}>{i18n.t('components.menu.back')}</Back>
      </ScreenTitleContainer>
      {screenStatus !== 'fetched' || !business ? (
        screenStatus === 'loading' ? (
          <StatusContainer>
            <Loader type="ThreeDots" color={Theme.colors.classicButtonLoader} height={30} />
          </StatusContainer>
        ) : (
          <ErrorLibelle>{i18n.t('components.detailsChantier.error')}</ErrorLibelle>
        )
      ) : (
        <>
          <TitleContainer>
            <Title>{i18n.t(`components.detailsChantier.title.${business.state}`, { title: business.title })}</Title>
            <Icon>
              {business.state === 'inprogress'
                ? inProgressMarker(30, business.source)
                : stoppedMarker(30, business.source)}
            </Icon>
          </TitleContainer>
          <Subtitle>{business.address}</Subtitle>
          <Categorisation>
            <ListContainer>
              <ListTitleContainer>
                <ListTitle
                  notHoverable={!selectedStep}
                  onClick={() => {
                    setSelectedStep(undefined)
                    setSelectedTheme(undefined)
                  }}>
                  <ListTitleLibelle>Étapes</ListTitleLibelle>
                  {!selectedStep ? <FiChevronDown size={15} /> : <FiChevronRight size={15} />}
                </ListTitle>
                {selectedStep && (
                  <ListTitle notHoverable={!selectedTheme} selected>
                    <ListTitleLibelle onClick={() => setSelectedTheme(undefined)}>
                      {!selectedTheme
                        ? i18n.t('components.detailsChantier.step', {
                            step: selectedStep.wording,
                            count: selectedPhoto.length,
                          })
                        : selectedStep.wording}
                    </ListTitleLibelle>
                    {!selectedTheme ? <FiChevronDown size={15} /> : <FiChevronRight size={15} />}
                  </ListTitle>
                )}
                {selectedTheme && (
                  <ListTitle notHoverable selected>
                    <ListTitleLibelle>
                      {i18n.t('components.detailsChantier.theme', {
                        theme: selectedTheme.wording,
                        count: selectedPhoto.length,
                      })}
                    </ListTitleLibelle>
                    <FiChevronDown size={15} />
                  </ListTitle>
                )}
              </ListTitleContainer>
              {!selectedStep
                ? steps
                    .sort((a, b) => a.index - b.index)
                    .map((step) => {
                      const currentStepPhotoCount = business.declarations.reduce(
                        (acc, cur) => (cur.description.idStep === step.id ? acc + cur.pictures.length : acc),
                        0
                      )
                      return (
                        <ListItemContainer key={step.id} onClick={() => setSelectedStep(step)}>
                          <FiChevronRight size={15} />
                          <ListItem>
                            {i18n.t('components.detailsChantier.step', {
                              step: step.wording,
                              count: currentStepPhotoCount,
                            })}
                          </ListItem>
                        </ListItemContainer>
                      )
                    })
                : !selectedTheme
                ? selectedStep.themes
                    .sort((a, b) => a.index - b.index)
                    .map((theme) => {
                      const currentThemePhotoCount = business.declarations.reduce(
                        (acc, cur) =>
                          cur.description.idStep === selectedStep.id && cur.description.idTheme === theme.id
                            ? acc + cur.pictures.length
                            : acc,
                        0
                      )
                      return (
                        <ListItemContainer key={theme.id} onClick={() => setSelectedTheme(theme)}>
                          <FiChevronRight size={15} />
                          <ListItem>
                            {i18n.t('components.detailsChantier.theme', {
                              theme: theme.wording,
                              count: currentThemePhotoCount,
                            })}
                          </ListItem>
                        </ListItemContainer>
                      )
                    })
                : null}
            </ListContainer>
            {selectedPhoto.length !== 0 &&
              (!downloading ? (
                <DownloadAction onClick={() => setDownloading(true)}>
                  <BsDownload size={30} color={Theme.colors.filterPrimarycolor} />
                </DownloadAction>
              ) : (
                <DownloadAction>
                  <Loader type="ThreeDots" color={Theme.colors.classicButtonLoader} height={10} />
                </DownloadAction>
              ))}
          </Categorisation>
          <ImagesContainer
            as={motion.div}
            className="container"
            variants={container}
            initial="hidden"
            animate="visible">
            {business.declarations.length === 0 ? (
              <NoPhotoLibelle>{i18n.t('components.detailsChantier.noPhoto')}</NoPhotoLibelle>
            ) : (
              selectedPhoto.map((p, idx) => (
                <motion.div key={p.id} className="item" variants={idx > 20 ? undefined : item}>
                  <Images
                    key={p.id}
                    imageId={p.id}
                    index={idx}
                    images={selectedPhoto}
                    status={p.status}
                    deletePicture={confirmDeletePhoto}
                  />
                </motion.div>
              ))
            )}
            <BottomContainer>
              <ButtonContainer>
                <Button
                  type="action"
                  title={i18n.t('components.detailsChantier.buttons.visualizePhotos')}
                  onClick={() => navigation.push(`/business/pictures/${business.id}`)}
                  status="active"
                />
                <Button
                  type="navigation"
                  title={i18n.t('components.detailsChantier.buttons.chantierDetails')}
                  onClick={() => navigation.push(`/business/${business.id}`)}
                  status="active"
                />
              </ButtonContainer>
            </BottomContainer>
          </ImagesContainer>
          {downloading && (
            <DownloadingComponent
              pictures={selectedPhoto.map((p) => ({
                id: p.id,
                path: '',
                name: `${business.title}_${p.step}_${p.theme}`,
              }))}
              cancel={() => setDownloading(false)}
            />
          )}
        </>
      )}
    </Container>
  )
}

export default BusinessFocusMenu

const Container = styled('div')`
  flex: 1;
  padding: 10px;
  display: flex;
  overflow: scroll;
  margin-bottom: 10px;
  flex-direction: column;
`
const StatusContainer = styled('div')`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
`
const ErrorLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
  margin: 40px;
`
const NoPhotoLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
  margin: 40px;
`
const Title = styled('div')`
  ${(props) => props.theme.fonts.chantierMenuTitle};
`
const TitleContainer = styled('div')`
  border: 0px solid ${(props) => props.theme.colors.menuBorder};
  border-bottom-width: 1px;
  padding: 15px 0px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`
const Icon = styled('div')`
  justify-content: center;
  align-items: center;
  display: flex;
`
const Subtitle = styled('div')`
  ${(props) => props.theme.fonts.chantierMenuSubtitle};
  border: 0px solid ${(props) => props.theme.colors.menuBorder};
  border-bottom-width: 1px;
  padding: 15px 0px;
`
const ImagesContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
  padding-top: 10px;
  padding-bottom: 200px;
`
const ButtonContainer = styled('div')`
  display: flex;
  height: 150px;
  align-items: center;
  flex-direction: column;
  justify-content: space-around;
`
const BottomContainer = styled('div')`
  bottom: 0px;
  display: flex;
  position: fixed;
  min-height: 200px;
  width: 430px;
  align-items: center;
  flex-direction: column;
  justify-content: flex-end;
  background: linear-gradient(359.87deg, #ffffff 50%, #00000000 100%);
  z-index: 10;
`
const ListContainer = styled('div')`
  display: flex;
  flex-direction: column;
  padding-bottom: 10px;
  border: 0px solid ${(props) => props.theme.colors.menuBorder};
  border-bottom-width: 1px;
  flex: 1;
`
const ListTitleContainer = styled('div')`
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  margin: 5px 6px;
`
const ListTitle = styled('div')<{ notHoverable: boolean; selected?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  ${(props) =>
    !props.notHoverable &&
    `cursor: pointer;
  &:hover {
    color: #0053a1;
    & > div {
      color: #0053a1;
    }
  }`}
  ${(props) =>
    props.selected &&
    `
    color: #0053A1;
    &>div{
      font-weight: 800;
      color: #0053A1;
  }`}
`
const ListTitleLibelle = styled('div')`
  ${(props) => props.theme.fonts.chantierMenuLibelle};
  text-decoration-line: underline;
  flex: none;
  order: 0;
  flex-grow: 0;
`
const ListItemContainer = styled('div')`
  display: flex;
  width: fit-content;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  padding-left: 20px;
  cursor: pointer;
  &:hover {
    color: #0053a1;
    & > div {
      color: #0053a1;
    }
  }
`
const ListItem = styled('div')`
  text-decoration-line: underline;
  flex: none;
  order: 1;
  flex-grow: 0;
  margin: 0px 5px;
  ${(props) => props.theme.fonts.chantierMenuItem};
`

const Back = styled('div')`
  ${(props) => props.theme.fonts.backLibelle};
  cursor: pointer;
`

const ScreenTitle = styled('div')`
  ${(props) => props.theme.fonts.title};
`
const ScreenTitleContainer = styled('div')`
  border: 0px solid ${(props) => props.theme.colors.menuBorder};
  border-bottom-width: 1px;
  border-top-width: 1px;
  padding: 15px 0;

  display: flex;
  align-items: center;
  justify-content: space-between;
`
const Categorisation = styled('div')`
  display: flex;
  flex-direction: row;
`
const DownloadAction = styled('div')`
  padding: 5px;
  cursor: pointer;
  height: fit-content;
  display: flex;
  align-items: center;
  justify-content: center;
`
